import React from 'react';
import PropTypes from 'prop-types';
import { Button, Card } from 'reactstrap';
import { IconExternalLink } from '@tabler/icons-react';
import { mergeClassNames } from '@core/shared-utils';
import classes from './ECB.module.scss';

/**
 * Adapter an der Komponenten-Grenze: normalisiert beide historisch gewachsenen
 * `assets`-Formen der Backends auf eine Breakpoint-Liste. Der Kern trifft damit
 * keine Annahme über das Datenformat.
 *
 * - `{ sm, md }` (Bigfoot): zwei feste Viewport-Bilder, umgeschaltet bei 768px.
 * - `[{ minWidth, url, … }]` (Cassiopeia): beliebig viele Breakpoints.
 *
 * Rückgabe: `breakpoints` absteigend nach `minWidth` sortiert (für `<source>`)
 * und `imgSrc` als Fallback-`<img>`-Quelle. Für die Objektform ist das wie
 * bisher das größte Bild, für die Arrayform der kleinste Breakpoint.
 */
export const normalizeEcbAssets = (assets) => {
    if (Array.isArray(assets)) {
        const breakpoints = assets
            .filter((bp) => bp && bp.url)
            .map((bp) => ({ minWidth: bp.minWidth ?? 0, url: bp.url }))
            .sort((a, b) => b.minWidth - a.minWidth);

        return {
            breakpoints,
            imgSrc: breakpoints.length > 0 ? breakpoints[breakpoints.length - 1].url : undefined,
        };
    }

    const sm = assets?.sm;
    const md = assets?.md;
    const breakpoints = [];
    if (md) breakpoints.push({ minWidth: 768, url: md });
    if (sm) breakpoints.push({ minWidth: 0, url: sm });

    return { breakpoints, imgSrc: md || sm };
};

/**
 * Adapter für `link.buttonPositioning`: akzeptiert den String (Cassiopeia, eine
 * Position für alle Viewports) und das Objekt `{ sm, md }` (Bigfoot).
 */
export const normalizeButtonPositioning = (positioning, fallback = 'center') => {
    if (typeof positioning === 'string') {
        const position = positioning.toLowerCase();
        return { sm: position, md: position };
    }

    const sm = (positioning?.sm || fallback).toLowerCase();
    const md = (positioning?.md || sm).toLowerCase();
    return { sm, md };
};

/**
 * Embedded Customer Branding: Bild mit optionalem Button darüber oder darunter.
 *
 * Die Produkte unterscheiden sich historisch in drei Punkten, die als Props an
 * der Aufrufstelle gesetzt werden (keine Produkt-Konditionale im Kern):
 * `variant` (flächiger Block vs. Bootstrap-Card), `buttonOnImage` und
 * `defaultButtonTextColor` greifen, wenn das Backend-Feld fehlt.
 */
const ECB = ({
    assets,
    altText,
    link,
    index = 0,
    variant = 'plain',
    buttonOnImage = true,
    defaultButtonTextColor = 'dark',
    defaultButtonPosition = 'center',
    analyticsIndexBase = 1,
}) => {
    // eTracker wertet die Button-Klasse aus. Sie war historisch in Bigfoot
    // 1-basiert (`index + 1`) und in Cassiopeia 0-basiert (`index`); der
    // Basiswert kommt deshalb von der Aufrufstelle.
    const analyticsIndex = index + analyticsIndexBase;
    // Die Link-Klasse war in beiden Produkten 1-basiert und bleibt es.
    const linkAnalyticsIndex = index + 1;
    const showBtn = Boolean(link?.buttonVisibility && link?.buttonText);
    const linkOnly = Boolean(link?.linkUrl && !showBtn);

    const { breakpoints, imgSrc } = normalizeEcbAssets(assets);
    const { sm: btnPosSm, md: btnPosMd } = normalizeButtonPositioning(link?.buttonPositioning, defaultButtonPosition);

    const posClassMapSm = {
        left: classes.posSm_left,
        right: classes.posSm_right,
        center: classes.posSm_center,
        fullwidth: classes.posSm_fullwidth,
    };
    const posClassMapMd = {
        left: classes.posMd_left,
        right: classes.posMd_right,
        center: classes.posMd_center,
        fullwidth: classes.posMd_fullwidth,
    };

    const positionClassNames = mergeClassNames(
        posClassMapSm[btnPosSm] || classes.posSm_center,
        posClassMapMd[btnPosMd] || classes.posMd_center
    );

    const isButtonOnImage = Boolean((link?.buttonOnImage ?? buttonOnImage) && showBtn && imgSrc);

    const isCustomBg = link?.buttonColor === 'custom' && link?.customButtonColor;
    const bsColorProp = isCustomBg ? undefined : link?.buttonColor || 'primary';

    const textMode = link?.buttonTextColor?.mode || defaultButtonTextColor;
    const textClass = textMode === 'light' ? 'text-light' : textMode === 'dark' ? 'text-dark' : undefined;

    const inlineTextColor =
        textMode === 'custom' && link?.buttonTextColor?.custom ? { color: link.buttonTextColor.custom } : undefined;

    const isOutline = Boolean(link?.outline);

    const imageElement =
        breakpoints.length > 0 ? (
            <picture>
                {breakpoints.map((bp, bpIdx) => (
                    <source key={bpIdx} media={`(min-width: ${bp.minWidth}px)`} srcSet={bp.url} />
                ))}
                <img src={imgSrc} alt={altText || ''} className={`${classes.ECBImage} img-fluid`} loading="lazy" />
            </picture>
        ) : (
            imgSrc && (
                <img src={imgSrc} alt={altText || ''} className={`${classes.ECBImage} img-fluid`} loading="lazy" />
            )
        );

    const buttonElement = showBtn && (
        <div
            className={mergeClassNames(isButtonOnImage ? classes.ECBOverlay : classes.ECBCardBody, positionClassNames)}
        >
            <Button
                color={bsColorProp}
                outline={isOutline}
                href={link?.linkUrl || undefined}
                target={link?.linkTarget || undefined}
                rel={link?.linkTarget === '_blank' ? 'noopener noreferrer' : undefined}
                className={`${classes.ECBBtn} anl_ecb--button-${analyticsIndex}${textClass ? ` ${textClass}` : ''}${isOutline ? ` ${classes['is-outline']}` : ''}`}
                style={{
                    ...(!isOutline && isCustomBg
                        ? { backgroundColor: link.customButtonColor, borderColor: link.customButtonColor }
                        : null),
                    ...(isOutline && isCustomBg
                        ? {
                              color: inlineTextColor?.color || link.customButtonColor,
                              borderColor: link.customButtonColor,
                              backgroundColor: 'transparent',
                          }
                        : null),
                    ...(inlineTextColor && !isOutline ? inlineTextColor : null),
                }}
            >
                {link?.buttonText}
                {/https?:\/\/.*/.test(link?.linkUrl) && <IconExternalLink size={20} className="ms-1" />}
            </Button>
        </div>
    );

    const content = (
        <>
            {imageElement}
            {buttonElement}
        </>
    );

    const Wrapper = variant === 'card' ? Card : 'div';

    return (
        <Wrapper className={mergeClassNames(classes.ECBCard, variant === 'card' ? classes.isCard : classes.isPlain)}>
            {linkOnly ? (
                <a
                    aria-label={
                        link?.linkTarget === '_blank'
                            ? `Zur Seite ${link.linkUrl} (wird in neuem Tab geöffnet)`
                            : `Zur Seite ${link.linkUrl}`
                    }
                    href={link.linkUrl}
                    target={link.linkTarget}
                    rel="noopener"
                    className={`anl_ecb--link-${linkAnalyticsIndex} d-block text-decoration-none`}
                >
                    {content}
                </a>
            ) : (
                content
            )}
        </Wrapper>
    );
};

ECB.propTypes = {
    assets: PropTypes.oneOfType([
        PropTypes.shape({
            sm: PropTypes.string,
            md: PropTypes.string,
        }),
        PropTypes.arrayOf(
            PropTypes.shape({
                minWidth: PropTypes.number.isRequired,
                url: PropTypes.string.isRequired,
                focalPoint: PropTypes.shape({
                    x: PropTypes.number,
                    y: PropTypes.number,
                }),
                scale: PropTypes.number,
                maxHeight: PropTypes.number,
            })
        ),
    ]),
    altText: PropTypes.string,
    link: PropTypes.shape({
        linkUrl: PropTypes.string,
        linkTarget: PropTypes.string,
        buttonText: PropTypes.string,
        buttonColor: PropTypes.string,
        customButtonColor: PropTypes.string,
        buttonTextColor: PropTypes.shape({
            mode: PropTypes.oneOf(['dark', 'light', 'custom', 'auto']),
            custom: PropTypes.string,
        }),
        outline: PropTypes.bool,
        buttonVisibility: PropTypes.bool,
        buttonOnImage: PropTypes.bool,
        buttonPositioning: PropTypes.oneOfType([
            PropTypes.oneOf(['left', 'center', 'right', 'fullwidth']),
            PropTypes.shape({
                sm: PropTypes.oneOf(['left', 'center', 'right', 'fullwidth']),
                md: PropTypes.oneOf(['left', 'center', 'right', 'fullwidth']),
            }),
        ]),
    }),
    index: PropTypes.number,
    variant: PropTypes.oneOf(['plain', 'card']),
    buttonOnImage: PropTypes.bool,
    defaultButtonTextColor: PropTypes.oneOf(['dark', 'light', 'custom', 'auto']),
    defaultButtonPosition: PropTypes.oneOf(['left', 'center', 'right', 'fullwidth']),
    /** 0 = Cassiopeia (0-basierte `anl_ecb--button-*`), 1 = Bigfoot (Default). */
    analyticsIndexBase: PropTypes.oneOf([0, 1]),
};

export default ECB;
